import { useEffect } from 'react';
import { Alert, App, Form, Input, Modal, Select } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys, type Chain, type ChainStatus } from './api';
import { ImageUploadField } from '../../components/ImageUploadField';

interface FormValues {
  name: string;
  slug: string;
  description?: string;
  coverImageUrl?: string;
  logoUrl?: string;
  zaloUrl?: string;
  status: ChainStatus;
}

const STATUS_OPTIONS: { value: ChainStatus; label: string }[] = [
  { value: 'Active', label: 'Đang hoạt động' },
  { value: 'Inactive', label: 'Ngừng hoạt động' },
];

/** Chuỗi rỗng/toàn khoảng trắng → null, để không lưu "" vào cột (khối "Giới thiệu" gate theo trim rỗng). */
const orNull = (value?: string): string | null => {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? null : trimmed;
};

interface Props {
  /** Khác null là đang SỬA chuỗi này. */
  chain: Chain | null;
  /** Bật để TẠO chuỗi mới (khi `chain` là null). */
  creating: boolean;
  onClose: () => void;
}

/** Chỉ super_admin thấy nút mở modal này — xem `CatalogPage`. */
export function ChainFormModal({ chain, creating, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const isEditing = chain !== null;
  const open = isEditing || creating;

  useEffect(() => {
    if (!open) return;

    if (chain !== null) {
      form.setFieldsValue({
        name: chain.name,
        slug: chain.slug,
        description: chain.description ?? undefined,
        coverImageUrl: chain.coverImageUrl ?? undefined,
        logoUrl: chain.logoUrl ?? undefined,
        zaloUrl: chain.zaloUrl ?? undefined,
        status: chain.status,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: 'Active' });
    }
  }, [open, chain, form]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const shared = {
        name: values.name,
        description: orNull(values.description),
        coverImageUrl: orNull(values.coverImageUrl),
        logoUrl: orNull(values.logoUrl),
        zaloUrl: orNull(values.zaloUrl),
      };

      // Slug là khóa cấu hình engine theo chuỗi — chỉ gửi khi TẠO, sửa thì backend bỏ qua.
      return chain !== null
        ? catalogApi.updateChain(chain.id, { ...shared, status: values.status })
        : catalogApi.createChain({ ...shared, slug: values.slug });
    },
    onSuccess: async (saved) => {
      // Ảnh bìa/giới thiệu của chuỗi cũng chảy xuống Member App qua `GET /catalog/chains`,
      // nhưng đây là cache của Admin nên chỉ cần làm mới danh sách chuỗi ở Admin.
      await queryClient.invalidateQueries({ queryKey: catalogKeys.chains });
      message.success(isEditing ? `Đã lưu ${saved.name}.` : `Đã tạo chuỗi ${saved.name}.`);
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      width={560}
      title={isEditing ? `Sửa ${chain.name}` : 'Thêm chuỗi'}
      okText={isEditing ? 'Lưu thay đổi' : 'Tạo chuỗi'}
      cancelText="Hủy"
      confirmLoading={mutation.isPending}
      okButtonProps={{ disabled: mutation.isPending }}
      onCancel={() => {
        mutation.reset();
        onClose();
      }}
      onOk={() => void form.submit()}
      destroyOnHidden
    >
      {mutation.isError && (
        <Alert
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          message={
            mutation.error instanceof ApiError || mutation.error instanceof NetworkError
              ? mutation.error.message
              : 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.'
          }
        />
      )}

      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item
          label="Tên chuỗi"
          name="name"
          rules={[{ required: true, message: 'Chưa nhập tên chuỗi.' }]}
        >
          <Input autoFocus maxLength={200} />
        </Form.Item>

        <Form.Item
          label="Mã định danh"
          name="slug"
          tooltip={
            isEditing
              ? 'Không đổi được sau khi tạo — là khóa của cấu hình tích điểm và quà theo chuỗi.'
              : 'Chữ thường, số và dấu gạch ngang. Dùng trong URL và báo cáo, không đổi được sau khi tạo.'
          }
          rules={
            isEditing
              ? []
              : [
                  { required: true, message: 'Chưa nhập mã định danh chuỗi.' },
                  {
                    pattern: /^[a-z0-9-]+$/,
                    message: 'Mã định danh chỉ gồm chữ thường, số và dấu gạch ngang.',
                  },
                ]
          }
        >
          {/* Slug không đổi được khi sửa — hiện để tham chiếu nhưng khóa nhập. */}
          <Input maxLength={50} disabled={isEditing} />
        </Form.Item>

        <Form.Item
          label="Giới thiệu"
          name="description"
          tooltip="Hiện ở khối “Giới thiệu” trên màn thương hiệu của Member App. Để trống thì khối ẩn hẳn."
        >
          <Input.TextArea rows={4} maxLength={2000} showCount />
        </Form.Item>

        <Form.Item
          label="Ảnh bìa"
          name="coverImageUrl"
          tooltip="Ảnh nền đầu màn thương hiệu. Để trống thì Member App dùng nền gradient mặc định."
          rules={[{ max: 500, message: 'Đường dẫn ảnh bìa quá dài (tối đa 500 ký tự).' }]}
        >
          <ImageUploadField />
        </Form.Item>

        <Form.Item
          label="Logo"
          name="logoUrl"
          rules={[{ max: 500, message: 'Đường dẫn logo quá dài (tối đa 500 ký tự).' }]}
        >
          <ImageUploadField />
        </Form.Item>

        <Form.Item
          label="Link Zalo OA"
          name="zaloUrl"
          tooltip="Zalo chung của thương hiệu, không phải của từng quán. Để trống thì Member App ẩn hẳn nút Zalo."
          rules={[
            { max: 500, message: 'Link Zalo quá dài (tối đa 500 ký tự).' },
            {
              pattern: /^https?:\/\/.+/i,
              message: 'Link Zalo phải bắt đầu bằng https:// hoặc http://.',
            },
          ]}
        >
          <Input placeholder="https://zalo.me/..." maxLength={500} />
        </Form.Item>

        {isEditing && (
          <Form.Item label="Trạng thái" name="status">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
